/**
 * CRM 应用根组件 — CrmApp (<crm-app>)
 *
 * 这是 CRM 前端应用的入口组件，负责：
 * 1. 渲染顶部导航栏（桌面端）和侧滑菜单（移动端）
 * 2. 根据路由切换页面内容（renderPage 方法）
 * 3. 管理用户权限驱动的导航项可见性
 * 4. 轮询客户跟进提醒（每 60 秒）并展示通知下拉
 * 5. 处理登录/登出流程
 *
 * 设计要点：
 * - 继承 BaseElement 以获得 Tailwind 样式注入
 * - 通过 router 单例监听 'crm-navigate' 事件实现路由切换
 * - 通过 authStore 订阅用户状态变化（用户和权限更新）
 * - 桌面端使用顶部导航栏 + 下拉子菜单；移动端使用侧滑抽屉
 * - 跟进提醒通过定时轮询获取，hover 时显示详情弹窗
 *
 * 关联模块：
 * - ./router.js — 路由控制
 * - ./store.js — 全局状态管理（authStore）
 * - ./api.js — API 调用（isLoggedIn, followUpApi）
 * - ./components/toast.js — 全局 Toast 通知
 * - ./pages/*.js — 各页面组件
 */
import { css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { router } from './router.js';
import { authStore } from './store.js';
import { followUpApi } from './api.js';
import { toast } from './components/toast.js';
import { BaseElement } from './base-element.js';
import { isNetworkError } from './utils/index.js';
// 以下 import 语句注册各页面和公共组件的自定义元素（副作用导入）
import './components/toast.js';
import './components/select.js';
import './components/side-panel.js';
import './components/loading-overlay.js';
import './components/table-toolbar.js';
import type { FollowUpReminder } from './types.js';
import './pages/login.js';
import './pages/customers.js';
import './pages/emails.js';
import './pages/users.js';
import './pages/email-accounts.js';
import './pages/permissions.js';
import './pages/email-templates.js';
import './pages/email-tasks.js';
import './pages/email-blacklist.js';
import './pages/dashboard.js';
import './pages/products.js';
import './pages/news.js';
import './pages/contacts.js';
import './pages/about.js';
import './pages/resources.js';
import './pages/settings.js';
import './pages/not-found.js';

/**
 * 导航菜单分组配置
 *
 * 定义顶部导航栏和移动端侧滑菜单的结构。
 * - 顶级项有 path 属性时为直接链接，无 path 时为下拉分组
 * - permission 定义菜单项所需的模块权限码
 * - children 数组定义下拉子菜单项
 */
const NAV_GROUPS: Array<{
  path?: string;
  label: string;
  icon: string;
  permission?: string;
  children?: Array<{ path: string; label: string; icon: string; permission?: string }>;
}> = [
  { path: 'dashboard', label: '数据概览', icon: 'bar-chart' },           // 首页仪表盘
  { path: 'customers', label: '客户', icon: 'users', permission: 'customer:manage' },
  { path: 'products', label: '产品', icon: 'box' },                     // 产品管理
  { path: 'emails', label: '邮件', icon: 'mail', permission: 'email:manage' },
  {
    label: '网站功能', icon: 'globe',                                    // 网站功能分组（下拉）
    children: [
      { path: 'about', label: 'About 管理', icon: 'file-text', permission: 'website:manage' },
      { path: 'news', label: '新闻管理', icon: 'newspaper' },           // 官网新闻
      { path: 'contacts', label: '联系管理', icon: 'message-square' },  // 官网联系表单
    ],
  },
  {
    label: '邮件功能', icon: 'send',                                     // 邮件功能分组（下拉）
    children: [
      { path: 'email-accounts', label: '邮箱账户', icon: 'at-sign', permission: 'email:manage' },
      { path: 'email-templates', label: '邮件模板', icon: 'layers', permission: 'template:manage' },
      { path: 'email-tasks', label: '邮件任务', icon: 'send', permission: 'task:manage' },
      { path: 'email-blacklist', label: '黑名单', icon: 'ban', permission: 'blacklist:manage' },
      { path: 'resources', label: '资源库', icon: 'bookmark', permission: 'resource:manage' },
    ],
  },
  {
    label: '管理', icon: 'shield',
    children: [
      { path: 'users', label: '用户管理', icon: 'user-plus', permission: 'user:manage' },
      { path: 'permissions', label: '权限管理', icon: 'shield', permission: 'permission:manage' },
    ],
  },
];

/**
 * SVG 图标库 — 内联 SVG 图标定义
 *
 * 使用 Lit 的 html 模板字面量存储 SVG，通过 ICONS[name] 引用。
 * 所有图标使用相同的视觉规范：24x24 viewBox，stroke 风格，currentColor 继承。
 */
const ICONS: Record<string, ReturnType<typeof html>> = {
  'bar-chart': html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="20" x2="12" y2="10"/><line x1="18" y1="20" x2="18" y2="4"/><line x1="6" y1="20" x2="6" y2="16"/></svg>`,
  users: html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>`,
  mail: html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>`,
  box: html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>`,
  'user-plus': html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/></svg>`,
  'at-sign': html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="4"/><path d="M16 8v5a3 3 0 0 0 6 0v-1a10 10 0 1 0-4 8"/></svg>`,
  shield: html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/></svg>`,
  'file-text': html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"/><path d="M14 2v4a2 2 0 0 0 2 2h4"/><line x1="16" x2="8" y1="13" y2="13"/><line x1="16" x2="8" y1="17" y2="17"/><line x1="10" x2="8" y1="9" y2="9"/></svg>`,
  layers: html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83Z"/><path d="m22 12.65-9.17 4.16a2 2 0 0 1-1.66 0L2 12.65"/><path d="m22 17.65-9.17 4.16a2 2 0 0 1-1.66 0L2 17.65"/></svg>`,
  'ban': html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg>`,
  send: html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m22 2-7 20-4-9-9-4z"/><path d="M22 2 11 13"/></svg>`,
  menu: html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="4" x2="20" y1="12" y2="12"/><line x1="4" x2="20" y1="6" y2="6"/><line x1="4" x2="20" y1="18" y2="18"/></svg>`,
  globe: html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M2 12h20"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>`,
  newspaper: html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 22h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v16a2 2 0 0 1-2 2Zm0 0a2 2 0 0 1-2-2v-9c0-1.1.9-2 2-2h2"/><path d="M18 14h-8"/><path d="M15 18h-5"/><path d="M10 6h8v4h-8z"/></svg>`,
  'message-square': html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>`,
  bookmark: html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m19 21-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16z"/></svg>`,
  x: html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>`,
  logout: html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>`,
  bell: html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"/></svg>`,
  'cog': html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/><circle cx="12" cy="12" r="3"/></svg>`,
};

/**
 * CRM 应用根组件
 *
 * 负责整体布局、导航渲染、路由切换和全局状态管理。
 * 通过 @customElement('crm-app') 装饰器注册为自定义元素。
 */
@customElement('crm-app')
export class CrmApp extends BaseElement {
  /** 当前页面路径标识，初始化为路由器的当前路径 */
  @state()
  private currentPage = router.currentPath;

  /** 当前展开的下拉菜单组标签（鼠标悬停时设置，离开后延时关闭） */
  @state()
  private activeDropdown: string | null = null;

  /** 下拉菜单关闭延时定时器（用于实现鼠标移出后的延迟关闭效果） */
  private dropdownTimer: number | null = null;

  /** 移动端侧滑菜单是否展开 */
  @state()
  private mobileMenuOpen = false;
  /** 标记鼠标按下时是否在遮罩层上（用于区分点击遮罩关闭 vs 拖动选择） */
  private _backdropMousedown = false;

  /** 当前用户角色（从 authStore 获取，用于控制管理员菜单可见性） */
  @state()
  private userRole: string | null = null;

  /** 当前权限列表，用于在权限加载或变更后刷新导航。 */
  @state()
  private userPermissions: string[] = [];

  /** 首次会话与路由校验是否完成。 */
  @state()
  private routeReady = false;

  /** 待跟进提醒列表（从服务端轮询获取） */
  @state() private followUps: FollowUpReminder[] = [];
  /** 是否显示跟进提醒下拉面板 */
  @state() private showFollowUpDropdown = false;
  /** 当前鼠标悬停的跟进提醒 ID（用于显示详情弹窗） */
  @state() private hoverFollowUpId: number | null = null;
  /** 悬停详情弹窗的定位坐标 */
  @state() private hoverFollowUpPos = { x: 0, y: 0 };
  /** 用户菜单是否展开 */
  @state() private showUserMenu = false;
  /** 跟进提醒轮询定时器（每 60 秒获取一次） */
  private _followUpPollTimer: number | null = null;

  /** authStore 订阅取消函数（组件卸载时调用以避免内存泄漏） */
  private _unsubscribe?: () => void;

  /** 组件样式：占满视口高度，隐藏溢出（由内部 main 区域滚动） */
  static styles = css`
    :host { display: block; height: 100vh; overflow: hidden; }
  `;

  /**
   * 组件连接到 DOM 时调用
   *
   * 1. 监听路由导航事件
   * 2. 从 authStore 获取用户角色并订阅变化
   * 3. 如果已登录，获取用户信息、首次获取跟进提醒、启动 60 秒轮询定时器
   */
  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('crm-navigate', this.handleNavigate);
    this.userRole = authStore.state.user?.role || null;
    this.userPermissions = [...authStore.state.permissions];
    this._unsubscribe = authStore.subscribe((state) => {
      this.userRole = state.user?.role || null;
      this.userPermissions = [...state.permissions];
    });
    void this.initializeApp();
  }

  /** 完成会话恢复和路由守卫后，再启动登录用户专属的数据轮询。 */
  private async initializeApp(): Promise<void> {
    await router.initialize();
    this.currentPage = router.currentPath;
    this.routeReady = true;
    if (router.isLoggedIn) {
      this.fetchFollowUps();
      // 每 60 秒轮询一次跟进提醒
      this._followUpPollTimer = window.setInterval(() => this.fetchFollowUps(), 60000);
    }
  }

  /**
   * 组件从 DOM 移除时调用
   *
   * 清理事件监听器、定时器和订阅，避免内存泄漏。
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('crm-navigate', this.handleNavigate);
    if (this._followUpPollTimer !== null) {
      window.clearInterval(this._followUpPollTimer);
      this._followUpPollTimer = null;
    }
    this._unsubscribe?.();
  }

  /**
   * 路由导航事件处理器
   *
   * 监听 'crm-navigate' 自定义事件，更新当前页面并关闭移动端菜单和下拉。
   */
  private handleNavigate = (e: Event) => {
    this.currentPage = (e as CustomEvent).detail.path;
    this.mobileMenuOpen = false;
    this.activeDropdown = null;
  };

  /**
   * 导航到指定页面
   *
   * @param path - 目标页面路径标识
   */
  private navigate(path: string) {
    router.navigate(path);
    this.mobileMenuOpen = false;
    this.activeDropdown = null;
  }

  /** 当前用户是否为管理员 */
  private get isAdmin() {
    return this.userRole === 'admin';
  }

  /** 判断当前用户是否具备导航项声明的模块权限。 */
  private canAccessPermission(permission?: string): boolean {
    if (!permission || this.isAdmin) return true;
    return this.userPermissions.includes(permission);
  }

  /**
   * 判断导航分组是否对当前用户可见
   *
   * @param group - 导航分组配置
   * @returns true 如果分组可见（非管理员专属或当前用户是管理员）
   */
  private isGroupVisible(group: typeof NAV_GROUPS[number]): boolean {
    if (!this.canAccessPermission(group.permission)) return false;
    if (group.children) return group.children.some((child) => this.canAccessPermission(child.permission));
    return true;
  }

  /** 切换移动端侧滑菜单的展开/收起状态 */
  private toggleMobileMenu() {
    this.mobileMenuOpen = !this.mobileMenuOpen;
  }

  /**
   * 处理用户登出
   *
   * 调用 authStore.logout() 清除 token 后重定向到首页。
   */
  private async handleLogout() {
    await authStore.logout();
    window.location.href = '/';
  }

  /**
   * 获取未完成的跟进提醒
   *
   * 请求 is_completed=0 的提醒列表，失败时静默处理（不打扰用户）。
   */
  private async fetchFollowUps() {
    try {
      const res = await followUpApi.list({ is_completed: 0 });
      this.followUps = res.data;
    } catch {
      // silent fail — 轮询失败不打扰用户
    }
  }

  /**
   * 标记跟进提醒为已完成
   *
   * @param id - 跟进提醒 ID
   */
  private async handleCompleteReminder(id: number) {
    try {
      await followUpApi.update(id, { is_completed: 1 });
      // 从列表中移除已完成的提醒
      this.followUps = this.followUps.filter(r => r.id !== id);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : '操作失败';
      // 网络错误不显示 toast（可能只是暂时断连）
      if (!isNetworkError(msg)) toast.error(msg);
    }
  }

  /**
   * 渲染顶部导航栏
   *
   * 包含两个部分：
   * 1. 桌面端顶部导航栏（md 以上显示）— 含 Logo、导航菜单组、跟进提醒铃铛、退出按钮
   * 2. 移动端头部栏 + 侧滑菜单（md 以下显示）— 含汉堡菜单按钮、Logo、侧滑抽屉
   *
   * @returns Lit html 模板
   */
  private renderTopNav() {
    // 判断导航分组是否处于激活状态（直接匹配 path 或子菜单中有匹配项）
    const isActive = (group: typeof NAV_GROUPS[number]): boolean => {
      if (group.path) return this.currentPage === group.path;
      return group.children?.some(c => this.currentPage === c.path) ?? false;
    };

    return html`
      <!-- Desktop Top Nav -->
      <header class="hidden md:flex items-center h-[54px] px-5 bg-surface border-b border-border z-40 gap-2 flex-shrink-0">
        <a href="/crm/dashboard" class="flex items-center gap-2 mr-5 no-underline cursor-pointer flex-shrink-0"
           @click=${(e: Event) => { e.preventDefault(); this.navigate('dashboard'); }}>
          <img src="/favicon.ico" alt="" class="w-[22px] h-[22px] rounded flex-shrink-0" />
          <span class="font-semibold text-base text-text tracking-tight whitespace-nowrap">RedSun</span>
        </a>

        ${NAV_GROUPS.filter(g => this.isGroupVisible(g)).map(group => {
          const active = isActive(group);
          if (group.path) {
            return html`
              <a href="/crm/${group.path}"
                 class="flex items-center gap-1.5 px-3 py-1.5 rounded-[7px] text-[13px] font-medium no-underline transition-colors cursor-pointer whitespace-nowrap
                        ${active ? 'bg-primary text-white' : 'text-text-secondary hover:bg-bg'}"
                 @click=${(e: Event) => { e.preventDefault(); this.navigate(group.path!); }}>
                <span class="flex-shrink-0 w-[18px] h-[18px]">${ICONS[group.icon]}</span>
                ${group.label}
              </a>`;
          }
          return html`
            <div class="relative"
                 @mouseenter=${() => {
                   if (this.dropdownTimer) { clearTimeout(this.dropdownTimer); this.dropdownTimer = null; }
                   this.activeDropdown = group.label;
                 }}
                 @mouseleave=${() => {
                   this.dropdownTimer = window.setTimeout(() => {
                     this.dropdownTimer = null;
                     this.activeDropdown = null;
                   }, 400);
                 }}>
              <span class="flex items-center gap-1.5 px-3 py-1.5 rounded-[7px] text-[13px] font-medium transition-colors cursor-pointer whitespace-nowrap select-none
                          ${active ? 'bg-primary text-white' : 'text-text-secondary hover:bg-bg'}">
                <span class="flex-shrink-0 w-[18px] h-[18px]">${ICONS[group.icon]}</span>
                ${group.label}
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" class="${active ? 'text-white/70' : 'text-text-muted'}"><path d="m6 9 6 6 6-6"/></svg>
              </span>
              ${this.activeDropdown === group.label ? html`
                <div class="absolute top-full left-0 mt-1.5 bg-surface border border-border rounded-[10px] py-1.5 min-w-[150px] shadow-[0_8px_24px_rgba(0,0,0,0.1)] z-50"
                     @mouseenter=${() => {
                       if (this.dropdownTimer) { clearTimeout(this.dropdownTimer); this.dropdownTimer = null; }
                     }}
                     @mouseleave=${() => {
                       this.activeDropdown = null;
                     }}>
                  ${(group.children || []).filter(c => this.canAccessPermission(c.permission)).map(child => html`
                    <a href="/crm/${child.path}"
                       class="flex items-center gap-2 px-3.5 py-2 text-[13px] no-underline transition-colors cursor-pointer whitespace-nowrap
                              ${this.currentPage === child.path ? 'text-primary bg-primary/5' : 'text-text-secondary hover:bg-bg'}"
                       @click=${(e: Event) => { e.preventDefault(); this.navigate(child.path); }}>
                      <span class="flex-shrink-0 w-[18px] h-[18px]">${ICONS[child.icon]}</span>
                      ${child.label}
                    </a>`)}
                </div>` : ''}
            </div>`;
        })}

        <!-- 弹性占位，将右侧按钮推到导航栏最右端 -->
        <span class="flex-1"></span>

        <!-- 跟进提醒铃铛按钮 + 下拉面板 -->
        <div class="relative">
          <button class="flex items-center gap-1.5 px-3 py-1.5 rounded-[7px] text-[13px] font-medium text-text hover:bg-bg transition-colors cursor-pointer relative"
                  @click=${(e: Event) => { e.stopPropagation(); this.showFollowUpDropdown = !this.showFollowUpDropdown; }}>
            <span class="flex-shrink-0 w-[18px] h-[18px]">${ICONS.bell}</span>
            ${this.followUps.length > 0 ? html`
              <!-- 未读提醒数量角标 -->
              <span class="absolute top-0.5 right-1 bg-error text-white text-[10px] rounded-full min-w-[16px] h-[16px] flex items-center justify-center px-1 font-semibold">${this.followUps.length}</span>
            ` : ''}
          </button>
          ${this.showFollowUpDropdown ? html`
            <!-- 点击外部区域关闭下拉（固定定位遮罩） -->
            <div class="fixed inset-0 z-40" @click=${() => this.showFollowUpDropdown = false}></div>
            <!-- 跟进提醒下拉面板 -->
            <div class="absolute right-0 top-full mt-1.5 bg-surface border border-border rounded-[10px] shadow-[0_8px_24px_rgba(0,0,0,0.1)] z-50 w-[576px] overflow-hidden">
              <div class="px-4 py-3 border-b border-border flex items-center gap-2">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-4 h-4 text-warning"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                <span class="font-semibold text-sm text-text">待跟进提醒 (${this.followUps.length})</span>
              </div>
              ${this.followUps.length > 0 ? html`
                <!-- 跟进提醒列表（可滚动，最大高度 320px） -->
                <div class="max-h-80 overflow-y-auto">
                  ${this.followUps.map(r => {
                    // 计算当前时间，判断是否逾期
                    const now = Math.floor(Date.now() / 1000);
                    const overdue = r.remind_at < now;
                    return html`
                      <div class="flex items-center gap-3 px-4 py-2.5 border-b border-border/50 hover:bg-bg/50 transition-colors ${overdue ? 'bg-error/5' : ''}"
                        @mouseenter=${(e: MouseEvent) => {
                          // 鼠标悬停时记录提醒 ID 并计算详情弹窗位置（弹窗显示在列表项左侧）
                          this.hoverFollowUpId = r.id;
                          const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                          this.hoverFollowUpPos = { x: rect.left - 308, y: rect.top };
                        }}
                        @mouseleave=${() => { this.hoverFollowUpId = null; }}>
                        <span class="text-xs font-mono ${overdue ? 'text-error font-semibold' : 'text-text-muted'} w-16 shrink-0">
                          ${new Date(r.remind_at * 1000).toLocaleDateString('zh-CN', { month: 'short', day: 'numeric' })}
                        </span>
                        <span class="text-sm font-medium text-text truncate flex-1 min-w-0">
                          ${r.customer_name || r.company_name || '(无名称)'}
                          ${overdue ? html`<span class="text-error text-xs ml-1">逾期</span>` : ''}
                        </span>
                        <span class="text-xs text-text-muted truncate max-w-[120px] hidden sm:inline">${r.note}</span>
                        <button class="text-xs font-medium text-success hover:text-success-hover px-2 py-1 rounded transition-colors cursor-pointer bg-transparent border-none shrink-0"
                          @click=${() => this.handleCompleteReminder(r.id)}>完成</button>
                      </div>
                    `;
                  })}
                </div>
              ` : html`<p class="text-sm text-text-muted text-center py-8">暂无待跟进提醒</p>`}
            </div>
            <!-- 悬停详情弹窗（固定定位，跟随鼠标悬停的列表项位置） -->
            ${this.hoverFollowUpId !== null ? html`
              <div class="fixed z-[60] bg-surface border border-border rounded-[10px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] p-3.5 w-[300px]"
                style="left: ${this.hoverFollowUpPos.x}px; top: ${this.hoverFollowUpPos.y}px;">
                ${(() => {
                  const r = this.followUps.find(f => f.id === this.hoverFollowUpId);
                  if (!r) return '';
                  const now = Math.floor(Date.now() / 1000);
                  const overdue = r.remind_at < now;
                  return html`
                    <div class="flex items-center gap-2 mb-2">
                      <span class="text-xs font-mono ${overdue ? 'text-error font-semibold' : 'text-text-muted'}">
                        ${new Date(r.remind_at * 1000).toLocaleDateString('zh-CN', { year: 'numeric', month: 'long', day: 'numeric' })}
                      </span>
                      ${overdue ? html`<span class="text-error text-xs font-medium">逾期</span>` : ''}
                    </div>
                    <div class="text-sm font-semibold text-text mb-1.5">${r.customer_name || r.company_name || '(无名称)'}</div>
                    ${r.note ? html`<div class="text-xs text-text-secondary leading-relaxed">${r.note}</div>` : ''}
                  `;
                })()}
              </div>
            ` : ''}
          ` : ''}
        </div>

        <!-- 用户菜单 -->
        <div class="relative">
          <button class="flex items-center gap-1.5 px-3 py-1.5 rounded-[7px] text-[13px] font-medium text-text hover:bg-bg transition-colors cursor-pointer whitespace-nowrap"
                  @click=${(e: Event) => { e.stopPropagation(); this.showUserMenu = !this.showUserMenu; }}>
            <span class="flex-shrink-0 w-[18px] h-[18px]">${ICONS.cog}</span>
          </button>
          ${this.showUserMenu ? html`
            <div class="fixed inset-0 z-40" @click=${() => this.showUserMenu = false}></div>
            <div class="absolute right-0 top-full mt-1.5 bg-surface border border-border rounded-[10px] py-1.5 min-w-[150px] shadow-[0_8px_24px_rgba(0,0,0,0.1)] z-50">
              <a href="/crm/settings"
                 class="flex items-center gap-2 px-3.5 py-2 text-[13px] no-underline transition-colors cursor-pointer whitespace-nowrap text-text-secondary hover:bg-bg"
                 @click=${(e: Event) => { e.preventDefault(); this.navigate('settings'); this.showUserMenu = false; }}>
                <span class="flex-shrink-0 w-[18px] h-[18px]">${ICONS.cog}</span>
                个人设置
              </a>
            </div>
          ` : ''}
        </div>

        <button class="flex items-center gap-1.5 px-3 py-1.5 rounded-[7px] text-[13px] font-medium text-text hover:bg-bg transition-colors cursor-pointer whitespace-nowrap"
                @click=${this.handleLogout}>
          <span class="flex-shrink-0 w-[18px] h-[18px]">${ICONS.logout}</span>
          退出
        </button>
      </header>

      <!-- Mobile Header -->
      <div class="md:hidden flex items-center gap-3 h-14 px-4 bg-surface border-b border-border flex-shrink-0">
        <button class="p-2 -ml-2 rounded hover:bg-bg cursor-pointer text-text-secondary" @click=${this.toggleMobileMenu} aria-label="打开菜单">
          <span class="w-5 h-5">${ICONS.menu}</span>
        </button>
        <span class="flex items-center gap-2"><img src="/favicon.ico" alt="" class="w-5 h-5 rounded" /><span class="font-semibold text-text tracking-tight">RedSun</span></span>
      </div>

      <!-- Mobile overlay — 半透明遮罩层，点击关闭侧滑菜单 -->
      ${this.mobileMenuOpen ? html`
        <!-- 使用 mousedown + click 双事件判断：仅当 mousedown 起始点在遮罩上时才关闭，
             避免用户在侧滑菜单内拖选文本释放到遮罩上时误触发关闭 -->
        <div class="md:hidden fixed inset-0 bg-black/40 z-40" @mousedown=${(e: Event) => { this._backdropMousedown = e.target === e.currentTarget; }} @click=${() => { if (this._backdropMousedown) this.toggleMobileMenu(); }}></div>
        <div class="md:hidden fixed top-0 left-0 h-full w-[260px] bg-surface z-50 shadow-xl flex flex-col">
          <div class="flex items-center justify-between h-16 px-4 border-b border-border">
            <span class="flex items-center gap-2"><img src="/favicon.ico" alt="" class="w-5 h-5 rounded" /><span class="font-semibold text-lg text-text">RedSun</span></span>
            <button class="p-2 rounded hover:bg-bg cursor-pointer text-text-muted" @click=${this.toggleMobileMenu} aria-label="关闭菜单">
              <span class="w-5 h-5">${ICONS.x}</span>
            </button>
          </div>
          <nav class="flex-1 overflow-y-auto py-4 px-3 space-y-1">
            ${NAV_GROUPS.filter(g => this.isGroupVisible(g)).map(group => {
              if (group.path) {
                return html`
                  <a href="/crm/${group.path}"
                     class="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium no-underline transition-colors cursor-pointer
                            ${this.currentPage === group.path ? 'bg-primary text-white' : 'text-text hover:bg-primary/5'}"
                     @click=${(e: Event) => { e.preventDefault(); this.navigate(group.path!); }}>
                    <span class="flex-shrink-0 w-5 h-5 ${this.currentPage === group.path ? 'text-white' : 'text-text-muted'}">${ICONS[group.icon]}</span>
                    <span>${group.label}</span>
                  </a>`;
              }
              return html`
                <div class="pb-1">
                  <div class="flex items-center gap-3 px-3 py-2 text-xs font-semibold text-text-secondary uppercase tracking-wider">${group.label}</div>
                  ${(group.children || []).filter(c => this.canAccessPermission(c.permission)).map(child => html`
                    <a href="/crm/${child.path}"
                       class="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm no-underline transition-colors cursor-pointer ml-2
                              ${this.currentPage === child.path ? 'bg-primary/10 text-primary font-semibold' : 'text-text-secondary hover:bg-bg'}"
                       @click=${(e: Event) => { e.preventDefault(); this.navigate(child.path); }}>
                      <span class="flex-shrink-0 w-4 h-4 ${this.currentPage === child.path ? 'text-primary' : 'text-text-muted'}">${ICONS[child.icon]}</span>
                      <span>${child.label}</span>
                    </a>`)}
                </div>`;
            })}
          </nav>
          <div class="border-t border-border p-3 space-y-1">
            <a href="/crm/settings" class="flex items-center gap-3 px-3 py-2.5 rounded-[8px] text-[13px] font-medium text-text-secondary hover:bg-bg transition-colors cursor-pointer no-decoration"
               @click=${(e: Event) => { e.preventDefault(); this.navigate('settings'); this.mobileMenuOpen = false; }}>
              <span class="flex-shrink-0 w-5 h-5 text-text">${ICONS.cog}</span>
              <span>个人设置</span>
            </a>
            <button class="w-full flex items-center gap-3 px-3 py-2.5 rounded text-sm text-text hover:bg-bg transition-colors cursor-pointer"
                    @click=${() => { this.handleLogout(); this.mobileMenuOpen = false; }}>
              <span class="flex-shrink-0 w-5 h-5 text-text">${ICONS.logout}</span>
              <span>退出登录</span>
            </button>
          </div>
        </div>
      ` : ''}
    `;
  }

  /**
   * 主渲染方法
   *
   * 根据当前页面和登录状态决定布局：
   * - 登录页和 404 页不显示导航栏，内容区占满全屏
   * - 其他页面显示顶部导航栏，内容区有内边距
   *
   * @returns Lit html 模板，包含 toast 容器和主内容区
   */
  render() {
    if (!this.routeReady) {
      return html`
        <toast-container></toast-container>
        <div class="relative h-screen bg-bg">
          <crm-loading-overlay active></crm-loading-overlay>
        </div>
      `;
    }
    const isLoginPage = this.currentPage === 'login';
    const isNotFound = this.currentPage === 'not-found';
    // 仅在非登录页、非404页且已登录时显示导航栏
    const showNav = !isLoginPage && !isNotFound && router.isLoggedIn;

    return html`
      <toast-container></toast-container>
      <div class="bg-bg h-screen flex flex-col">
        ${showNav ? this.renderTopNav() : ''}
        <main class="flex-1 overflow-y-auto ${showNav ? 'p-2 sm:p-3 lg:p-4' : ''}">
          ${this.renderPage()}
        </main>
      </div>
    `;
  }

  /**
   * 根据当前页面路径渲染对应的页面组件
   *
   * 通过 switch 语句将路由 path 映射到对应的自定义元素。
   * 每个页面组件在文件顶部通过 import 注册。
   *
   * @returns 对应页面的 Lit html 模板，未匹配时返回 404 页面
   */
  private renderPage() {
    switch (this.currentPage) {
      case 'dashboard':
        return html`<crm-dashboard-page></crm-dashboard-page>`;
      case 'login':
        return html`<crm-login-page></crm-login-page>`;
      case 'customers':
        return html`<crm-customers-page></crm-customers-page>`;
      case 'emails':
        return html`<crm-emails-page></crm-emails-page>`;
      case 'users':
        return html`<crm-users-page></crm-users-page>`;
      case 'email-accounts':
        return html`<crm-email-accounts-page></crm-email-accounts-page>`;
      case 'permissions':
        return html`<crm-permissions-page></crm-permissions-page>`;
      case 'email-templates':
        return html`<crm-email-templates-page></crm-email-templates-page>`;
      case 'email-tasks':
        return html`<crm-email-tasks-page></crm-email-tasks-page>`;
      case 'email-blacklist':
        return html`<crm-email-blacklist-page></crm-email-blacklist-page>`;
      case 'products':
        return html`<crm-products-page></crm-products-page>`;
      case 'news':
        return html`<crm-news-page></crm-news-page>`;
      case 'about':
        return html`<crm-about-page></crm-about-page>`;
      case 'contacts':
        return html`<crm-contacts-page></crm-contacts-page>`;
      case 'resources':
        return html`<crm-resources-page></crm-resources-page>`;
      case 'settings':
        return html`<crm-settings-page></crm-settings-page>`;
      default:
        return html`<crm-not-found-page></crm-not-found-page>`;
    }
  }
}
